import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';

interface LoanTypeSelectorProps {
  value: 'conventional' | 'fha' | 'va' | 'usda';
  onChange: (value: 'conventional' | 'fha' | 'va' | 'usda') => void;
  basePoints: string;
}

const LoanTypeSelector: React.FC<LoanTypeSelectorProps> = ({ value, onChange, basePoints }) => {
  const loanTypes = [
    {
      value: 'conventional' as const,
      label: 'Conventional',
      description: 'Standard mortgage with competitive rates',
      basePoints: '4.355'
    },
    {
      value: 'fha' as const,
      label: 'FHA',
      description: 'Lower credit score requirements',
      basePoints: '3.402'
    },
    {
      value: 'va' as const,
      label: 'VA',
      description: 'For eligible veterans and service members',
      basePoints: '3.402'
    },
    {
      value: 'usda' as const,
      label: 'USDA',
      description: 'Rural property financing',
      basePoints: '3.402'
    }
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-base font-semibold">Loan Type</Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Info className="w-4 h-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>Different loan types have different base point costs for the same 4.99% fixed rate</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      <RadioGroup value={value} onValueChange={onChange} className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {loanTypes.map((type) => (
          <label
            key={type.value}
            className={`flex items-center space-x-2 border rounded-lg p-3 cursor-pointer transition-all ${
              value === type.value 
                ? 'border-primary bg-primary/5 shadow-sm' 
                : 'border-border hover:border-primary/50 hover:bg-accent/50'
            }`}
          >
            <RadioGroupItem value={type.value} id={type.value} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{type.label}</div>
              <div className="text-xs text-muted-foreground truncate">{type.description}</div>
              <div className="text-xs font-mono text-primary mt-1">{type.basePoints} pts</div>
            </div>
          </label>
        ))}
      </RadioGroup>

      <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex justify-between items-center">
          <span className="font-medium text-sm">Base Rate Points (4.99% Fixed):</span>
          <span className="text-lg font-bold text-primary">{basePoints} points</span>
        </div>
      </div>
    </div>
  );
};

export default LoanTypeSelector;
